import {
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Switch,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';
import {
  getOrganisations,
  saveOrgUser,
} from '../../../stores/slices/organisations/organisationsActions';
import { useSelector } from 'react-redux';

const UserModal = ({ isOpen, onOpen, onClose, toUpdate, org, getUsers }) => {
  const { organisations } = useSelector(state => state.organisations);
  const schema = yup
    .object({
      firstName: yup.string().required('First mame is required'),
      lastName: yup.string().required('Last name is required'),
      role: yup.string().required('Role is required'),
      organisation: yup.string().required('Organisation is required'),
      email: yup
        .string()
        .email('Must be a valid email')
        .required('Email is required'),
      ...(!toUpdate.state
        ? {
            password: yup
              .string()
              .min(8, 'Must be at least 8 characters long')
              .required('Password is required'),
          }
        : {}),
    })
    .required();

  const dispatch = useDispatch();
  const toast = useToast();
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSave = async data => {
    let userData = {
      first_name: data.firstName,
      last_name: data.lastName,
      email: data.email,
      password: data.password,
      role: data.role,
      organisation_id: data.organisation,
      is_active: data.is_active,
      confirmed: data.confirmed,
    };
    if (!toUpdate.state) {
      userData = { ...userData, organisation_id: org.id };
    }
    const res = await dispatch(
      saveOrgUser({
        data: userData,
        isUpdate: toUpdate,
      })
    );

    if (res.type === 'organisations/saveOrgUser/rejected') {
      toast({
        title: 'Something went wrong',
        description: 'Please try again later.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    } else {
      reset();
      getUsers();
      onClose();
      if (parseInt(data.organisation) !== parseInt(org.id)) {
        dispatch(getOrganisations());
      }
      toast({
        description: toUpdate.state
          ? 'User has been updated'
          : 'User has been created',
        colorScheme: 'primary',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
        variant: 'primary',
      });
    }
  };
  return (
    <Modal
      closeOnOverlayClick={false}
      isOpen={isOpen}
      onClose={() => {
        reset();
        onClose();
      }}
    >
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSubmit(onSave)}>
          <ModalHeader>{toUpdate.state ? 'Edit' : 'Add new'} user</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={'2'}>
              <FormControl id="organisation" isInvalid={errors.organisation}>
                <FormLabel>Organisation</FormLabel>
                <Select
                  placeholder="Select option"
                  id="organisation"
                  {...register('organisation')}
                  defaultValue={
                    toUpdate.state ? toUpdate.user.organisation.id : ''
                  }
                >
                  {organisations.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>
                  {errors.organisation && errors.organisation.message}
                </FormErrorMessage>
              </FormControl>
              <FormControl id="firstName" isInvalid={errors.firstName}>
                <FormLabel>First Name</FormLabel>
                <Input
                  id="firstName"
                  {...register('firstName')}
                  defaultValue={toUpdate.state ? toUpdate.user.first_name : ''}
                />
                <FormErrorMessage>
                  {errors.firstName && errors.firstName.message}
                </FormErrorMessage>
              </FormControl>
              <FormControl id="lastName" isInvalid={errors.lastName}>
                <FormLabel>Last Name</FormLabel>
                <Input
                  id="lastName"
                  {...register('lastName')}
                  defaultValue={toUpdate.state ? toUpdate.user.last_name : ''}
                />
                <FormErrorMessage>
                  {errors.lastName && errors.lastName.message}
                </FormErrorMessage>
              </FormControl>
              <FormControl id="email" isInvalid={errors.email}>
                <FormLabel>Email address</FormLabel>
                <Input
                  id="email"
                  {...register('email')}
                  defaultValue={toUpdate.state ? toUpdate.user.email : ''}
                />
                <FormErrorMessage>
                  {errors.email && errors.email.message}
                </FormErrorMessage>
              </FormControl>
              {!toUpdate.state && (
                <FormControl id="password" isInvalid={errors.password}>
                  <FormLabel>Password</FormLabel>
                  <Input
                    id="password"
                    type={'password'}
                    {...register('password')}
                  />
                  <FormErrorMessage>
                    {errors.password && errors.password.message}
                  </FormErrorMessage>
                  <FormHelperText>
                    Your password must be 8-20 characters long, contain letters
                    and numbers, and must not contain spaces, or emoji.
                  </FormHelperText>
                </FormControl>
              )}
              <FormControl id="role" isInvalid={errors.role}>
                <FormLabel>Role</FormLabel>
                <Select
                  placeholder="Select option"
                  id="role"
                  {...register('role')}
                  defaultValue={toUpdate.state ? toUpdate.user.role : ''}
                >
                  <option value="employer">Account Admin</option>
                  <option value="manager">Manager</option>
                  <option value="employee">Standard User</option>
                </Select>
                <FormErrorMessage>
                  {errors.role && errors.role.message}
                </FormErrorMessage>
              </FormControl>
              <FormControl
                id="confirmed"
                isInvalid={errors.confirmed}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <FormLabel mb="0">Confirmed</FormLabel>
                <Switch
                  id="confirmed"
                  {...register('confirmed')}
                  defaultChecked={toUpdate.state ? toUpdate.user.confirmed : ''}
                  disabled={toUpdate.state ? toUpdate.user.confirmed : false}
                />

                <FormErrorMessage>
                  {errors.confirmed && errors.confirmed.message}
                </FormErrorMessage>
              </FormControl>
              <FormControl
                id="is_active"
                isInvalid={errors.is_active}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <FormLabel mb="0">Active</FormLabel>
                <Switch
                  id="is_active"
                  {...register('is_active')}
                  defaultChecked={toUpdate.state ? toUpdate.user.is_active : ''}
                />

                <FormErrorMessage>
                  {errors.is_active && errors.is_active.message}
                </FormErrorMessage>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} type="submit" isLoading={isSubmitting}>
              Save
            </Button>
            <Button
              onClick={() => {
                reset();
                onClose();
              }}
              variant="outline"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default UserModal;
